import React from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { prop } from 'styled-tools';
import CSSTransitionGroup from 'react-transition-group/CSSTransitionGroup';
import smoothScroll from 'smoothscroll';

import { IconButton } from 'components/Shared/Buttons';
import { GLYPHS } from 'components/Shared/Icon';
import { SCAFFOLDING, TYPOGRAPHY, TRANSITION } from 'theme';
import { FirstChild } from 'utils/transitions';

export const TRANSITION_TYPE = {
    FADE: 'fade',
    SLIDE: 'slide',
};

const propTypes = {
    open: PropTypes.bool.isRequired,
    children: PropTypes.node.isRequired,
    main: PropTypes.bool,
    onClose: PropTypes.func,
    transition: PropTypes.oneOf(['fade', 'slide']),
};

const defaultProps = {
    main: false,
    onClose: null,
    transition: TRANSITION_TYPE.SLIDE,
};

export const PanelWrapper = styled.section`
    position: absolute;
    top: 0;
    bottom: 0;
    width: ${prop('width', SCAFFOLDING.COLUMN_WIDTH(4))};
    float: left;
    z-index: 2;
    background-color: ${SCAFFOLDING.BG};

    &.${TRANSITION_TYPE.SLIDE} {
        /* Active must come after its previous state */
        &-enter {
            transform: translate3d(0, 100%, 0);
        }

        &-enter-active {
            transform: translate3d(0, 0, 0);
        }

        &-leave {
            transform: translate3d(0, 0, 0);
        }

        &-leave-active {
            transform: translate3d(0, 100%, 0);
        }

        /* Need the transition on the active classes to animate with react-select */
        &-enter-active,
        &-leave-active {
            transition: transform ${TRANSITION.LENGTH_LONG} ${TRANSITION.BEZIER};
        }
    }

    &.${TRANSITION_TYPE.FADE} {
        /* Active must come after its previous state */
        &-enter {
            opacity: 0;
        }

        &-enter-active {
            opacity: 1;
        }

        &-leave {
            opacity: 1;
        }

        &-leave-active {
            opacity: 0;
        }

        /* Need the transition on the active classes to animate with react-select */
        &-enter-active,
        &-leave-active {
            transition: opacity ${TRANSITION.LENGTH_LONG} ${TRANSITION.BEZIER};
        }
    }
`;

const PanelContent = styled.div`
    position: relative;
    height: 100%;
    overflow-x: hidden;
    overflow-y: auto;
    z-index: 2;
    -webkit-overflow-scrolling: touch;
`;

const PanelTitle = styled.h1`
    margin: 0;
`;

const ClosePanelButton = styled(IconButton)`
    position: absolute;
    top: ${SCAFFOLDING.GUTTER_FACTOR(0.25)};
    right: ${SCAFFOLDING.GUTTER_FACTOR(0.25)};
    width: ${SCAFFOLDING.GUTTER};
    height: ${SCAFFOLDING.GUTTER};
    z-index: 3;
    color: ${TYPOGRAPHY.COLOR};

    &:hover,
    &:focus {
        color: ${TYPOGRAPHY.LINK_COLOR.HOVER};
    }
`;

class Panel extends React.Component {
    handleScrollToTop = (context = this.colPanelContent) => {
        const scrollEl = ReactDOM.findDOMNode(context); // eslint-disable-line react/no-find-dom-node
        smoothScroll(0, parseInt(TRANSITION.LENGTH_LONG, 10), null, scrollEl);
    };

    render() {
        const { main, transition, ...props } = this.props;

        const closeButton = !main && (
            <ClosePanelButton title="Cancel Changes" glyph={GLYPHS.CLOSE} onClick={props.onClose} />
        );

        return (
            <CSSTransitionGroup
                component={FirstChild}
                transitionName={transition}
                transitionEnterTimeout={parseInt(TRANSITION.LENGTH_LONG, 10)}
                transitionLeaveTimeout={parseInt(TRANSITION.LENGTH_LONG, 10)}
            >
                {props.open ? (
                    <PanelWrapper width="100%" aria-expanded={props.open} {...props}>
                        {closeButton}
                        <PanelContent
                            ref={(el) => {
                                this.colPanelContent = el;
                            }}
                        >
                            {/* TODO: This requires the children to be components without throwing an unknown prop warning. See if there is a better solution to this.*/}
                            {React.Children.map(props.children, (child) =>
                                React.cloneElement(child, {
                                    scrollToTop: this.handleScrollToTop,
                                })
                            )}
                        </PanelContent>
                    </PanelWrapper>
                ) : null}
            </CSSTransitionGroup>
        );
    }
}

Panel.propTypes = propTypes;
Panel.defaultProps = defaultProps;

export default Panel;
export { PanelTitle };
